import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../auth/useAuth";
import { useRuntimeConfig } from "../config/useRuntimeConfig";
import { createHealthRecordServiceClient, type Allergy, type Appointment, type HealthCondition, type Vaccination } from "./client";

export type HealthOverviewStatus = "idle" | "loading" | "ready" | "error";

// Read-only aggregate loader for composite pages (the Health landing page). One request to
// health-record-service's GET /v1/overview replaces the four independent list loads for
// vaccinations, allergies, conditions and appointments -- the backend applies the same
// filtering/ordering/normalization and JWT / health_profile_id ownership as those endpoints.
// Same hook conventions as useVaccinations / useClinicalRecords / useAppointments: runtime-
// configured base URL, auth.user access token, AbortSignal, loading/error state, no cache.
export function useHealthOverview() {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const apiBaseUrl = config?.healthRecordService?.apiBaseUrl;
  const [vaccinations, setVaccinations] = useState<Vaccination[]>([]);
  const [allergies, setAllergies] = useState<Allergy[]>([]);
  const [conditions, setConditions] = useState<HealthCondition[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [status, setStatus] = useState<HealthOverviewStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const client = useMemo(() => {
    if (!apiBaseUrl) {
      return null;
    }

    return createHealthRecordServiceClient(apiBaseUrl, () => auth.user?.access_token);
  }, [apiBaseUrl, auth.user]);

  const loadHealthOverview = useCallback(
    async (signal?: AbortSignal) => {
      if (!client) {
        setError("Health records are not available yet.");
        setStatus("error");
        return;
      }

      setStatus("loading");
      setError(null);

      try {
        const overview = await client.listHealthOverview(signal);
        setVaccinations(overview.vaccinations);
        setAllergies(overview.allergies);
        setConditions(overview.conditions);
        setAppointments(overview.appointments);
        setStatus("ready");
      } catch (nextError) {
        if (nextError instanceof DOMException && nextError.name === "AbortError") {
          return;
        }

        // One resource-neutral message for the whole overview -- it covers all four domains, so the
        // per-resource wording of the individual list endpoints would be misleading here.
        setError("We could not reach your health records right now.");
        setStatus("error");
      }
    },
    [client],
  );

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return undefined;
    }

    const controller = new AbortController();
    void loadHealthOverview(controller.signal);

    return () => controller.abort();
  }, [auth.isAuthenticated, loadHealthOverview]);

  return {
    allergies,
    appointments,
    conditions,
    error,
    loadHealthOverview,
    status,
    vaccinations,
  };
}
